import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import {IAdditionalData} from '../../models/Extension';
import {useLazyGetExportExtensionOptionsQuery} from "../../store/Api";
import {
    CommandBar,
    ContextualMenuItemType,
    IColumn,
    ICommandBarItemProps,
    IconButton,
    MessageBar,
    MessageBarType,
    Selection,
    SelectionMode,
    ShimmeredDetailsList, Spinner, SpinnerSize,
    Text
} from "@fluentui/react";
import {toast} from 'react-toastify';
import {IDictionary} from "../../models/ICollectionResponse";
import FilterableSortableColumnHeader from "./SortableDirectImportPreviewHeader";
import {
    humanizeRecordType,
    IWorkspaceRecordLink,
    useLazyGetWorkspaceRecordLinksQuery,
    useLazyResolveRecordUrlQuery
} from "../../store/endpoints/RecordLinksEndpoints";

interface IExtensionSearchResultsProps {
    field: IAdditionalData;
    isDisabled?: boolean;
    extensionId: string;
    /** The source folio id when the extension is a folio sub-extension. Enables the per-row
     * "open in CRM" deep link when the field maps to a CRM record type. */
    crmFolioId?: string;
    /** The extension's display name — a fallback source for inferring the CRM record type
     * (e.g. "ConnectWise PSA Opportunity Import" → Opportunity) when the field name doesn't carry it. */
    extensionName?: string;
    /** When set, the paginator renders into this element (the panel header) instead of above the list. */
    paginatorHost?: HTMLElement | null;
    initialValue: string;
    onSelectOption?: (value: string) => void;
    workspaceId?: string;
}

/** CrmRecordType values the API can resolve deep links for, matched against the field name,
 * field description, and extension name (in that order). */
const CRM_RECORD_TYPES = ['Opportunity', 'PurchaseOrder', 'SalesOrder', 'Ticket', 'Configuration', 'Activity', 'Company', 'Contact', 'Location'];

/** The rows are untyped dictionaries, so display formatting is heuristic: numeric values in
 * money-named columns render as currency. */
const CURRENCY_COLUMN_PATTERN = /total|price|cost|revenue|amount|margin/i;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

/** Human-friendly cell text: currency for money columns, locale dates for ISO date strings.
 * Sorting and filtering still run on the raw values, which order correctly as-is. */
const formatCellValue = (columnKey: string, value: string | number | boolean | undefined): string => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'number' && CURRENCY_COLUMN_PATTERN.test(columnKey)) {
        return value.toLocaleString('en-US', {style: 'currency', currency: 'USD'});
    }
    if (typeof value === 'string') {
        const isoMatch = value.match(ISO_DATE_PATTERN);
        if (isoMatch) {
            const midnight = !isoMatch[4] || (isoMatch[4] === '00' && isoMatch[5] === '00' && isoMatch[6] === '00');
            if (midnight) {
                // Date-only values (e.g. an expected close date at midnight UTC) would shift a day if
                // converted to local time, so format the date digits directly.
                return new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3])).toLocaleDateString();
            }
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
                return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'})}`;
            }
        }
        return value;
    }
    return value.toString();
};

const ExtensionSearchResults: React.FC<IExtensionSearchResultsProps> = (props) => {
    const [optionValues, setOptionValues] = useState<(IDictionary<string | number | boolean>)[]>([])
    const [getExtensionOptions] = useLazyGetExportExtensionOptionsQuery();
    const [isLoading, setIsLoading] = useState<boolean>(!!props.initialValue)
    const [loadError, setLoadError] = useState<string>()
    const [filterField, setFilterField] = useState<string>()
    const [filterValue, setFilterValue] = useState<string>()
    const [sortField, setSortField] = useState<string>()
    const [sortDescending, setSortDescending] = useState<boolean>(false)
    const [page, setPage] = useState<number>(1)
    // 'fit' sizes each page to the space between the list and the panel footer so the panel never
    // needs an inner scrollbar; the paginator absorbs the length instead.
    const [pageSizeSetting, setPageSizeSetting] = useState<number | 'fit'>('fit')
    const [fitPageSize, setFitPageSize] = useState<number>(10)
    const listStartRef = useRef<HTMLDivElement>(null)

    const selectedIdRef = useRef<string>()
    const onSelectRef = useRef(props.onSelectOption)
    onSelectRef.current = props.onSelectOption

    useEffect(() => {
        if(props.initialValue){
            setIsLoading(true)
            getExtensionOptions({extensionId: props.extensionId, field: props.field.field, search: props.initialValue, additionalData: undefined, workspaceId: props.workspaceId})
                .unwrap().then((result) => {
                    // Extension failures come back as 200s with success=false — surface them instead of
                    // rendering an empty list.
                    setLoadError(result?.success === false ? (result.error || 'The extension failed to load results') : undefined)
                    if (result) {
                        const optionValues: IDictionary<string | number | boolean>[] = []
                        for(let i = 0; i < (result.options?.length ?? 0); i++){
                            if(result.options?.[i]?.additionalProperties) {
                                optionValues.push(result.options[i].additionalProperties as (IDictionary<string | number | boolean>))
                            }
                        }
                        setOptionValues(optionValues)
                        setPage(1)
                    }
                    setIsLoading(false)
                }
            ).catch(() => {
                setLoadError('The extension failed to load results')
                setIsLoading(false)
            })
        }
    }, [props.initialValue])

    // Measure how many rows fit between the top of the list and the panel footer. 43px per row plus
    // one header row; the panel's pinned footer plus body padding take ~90px of the window's bottom.
    const measureFitPageSize = () => {
        const top = listStartRef.current?.getBoundingClientRect().top;
        if (top == null) return;
        const available = window.innerHeight - top - 90;
        const rows = Math.floor(available / 43) - 1;
        setFitPageSize(Math.min(50, Math.max(5, rows)));
    }

    useLayoutEffect(() => {
        if (!isLoading && optionValues.length > 0) measureFitPageSize();
    }, [isLoading, optionValues])

    useEffect(() => {
        window.addEventListener('resize', measureFitPageSize);
        return () => window.removeEventListener('resize', measureFitPageSize);
    }, [])

    const selection = useMemo(() => {
        const s: Selection = new Selection({
            onSelectionChanged: () => {
                const item = s.getSelection()[0] as IDictionary<string | number | boolean> | undefined;
                if (!item) return;
                const value = item["Id"] ? item["Id"].toString() : "";
                if (value === selectedIdRef.current) return;
                selectedIdRef.current = value;
                onSelectRef.current?.(value);
            },
        });
        return s;
    }, [])

    const [resolveRecordUrl] = useLazyResolveRecordUrlQuery();
    const matchesRecordType = (source: string, recordType: string) => {
        const haystack = source.toLowerCase();
        const singular = recordType.toLowerCase();
        const plural = singular.endsWith('y') ? `${singular.slice(0, -1)}ies` : `${singular}s`;
        return haystack.includes(singular) || haystack.includes(plural);
    };
    const crmRecordType = props.crmFolioId
        ? [props.field.field, props.field.description, props.extensionName]
            .map(source => source && CRM_RECORD_TYPES.find(t => matchesRecordType(source, t)))
            .find(Boolean) || undefined
        : undefined;

    // Lazy check: which of the listed records already have a linked workspace (i.e. were imported).
    const [getWorkspaceRecordLinks] = useLazyGetWorkspaceRecordLinksQuery();
    const [workspaceLinksByRecord, setWorkspaceLinksByRecord] = useState<{[externalId: string]: IWorkspaceRecordLink}>();
    useEffect(() => {
        if (crmRecordType !== 'Opportunity' || optionValues.length === 0) {
            setWorkspaceLinksByRecord(undefined);
            return;
        }
        const externalIds = optionValues
            .map((o) => o["Id"]?.toString())
            .filter((v): v is string => !!v);
        if (externalIds.length === 0) return;
        getWorkspaceRecordLinks({field: 'opportunityId', externalIds}).unwrap()
            .then((links) => {
                const map: {[externalId: string]: IWorkspaceRecordLink} = {};
                links.forEach((link) => { map[link.externalId] = link; });
                setWorkspaceLinksByRecord(map);
            })
            .catch(() => setWorkspaceLinksByRecord(undefined));
    }, [optionValues, crmRecordType])

    const openInCrm = async (item: IDictionary<string | number | boolean>) => {
        const recordId = item["Id"]?.toString();
        if (!recordId || !props.crmFolioId || !crmRecordType) return;
        try {
            const res = await resolveRecordUrl({folioId: props.crmFolioId, recordType: crmRecordType, recordId}).unwrap();
            if (res.supported && res.url) {
                window.open(res.url, '_blank', 'noopener');
            } else {
                toast.error(`This ${humanizeRecordType(crmRecordType)} can't be opened externally`);
            }
        } catch {
            toast.error('Failed to open record');
        }
    };

    const sortColumn = (key: string) => {
        if (sortField === key) {
            setSortDescending(!sortDescending)
        } else {
            setSortField(key)
            setSortDescending(false)
        }
        setPage(1)
    }

    const applyFilter = (key: string, filter?: string) => {
        setFilterField(filter ? key : undefined)
        setFilterValue(filter)
        setPage(1)
    }

    const filteredValues = useMemo(() => {
        if (!filterField || !filterValue) return optionValues;
        const needle = filterValue.toLowerCase();
        // Match the raw value or the formatted one, so "9/30" finds an ISO date and "$1,2" a currency cell.
        return optionValues.filter((o) =>
            (o[filterField] ?? '').toString().toLowerCase().includes(needle) ||
            formatCellValue(filterField, o[filterField]).toLowerCase().includes(needle));
    }, [optionValues, filterField, filterValue])

    const sortedValues = useMemo(() => {
        if (!sortField) return filteredValues;
        const direction = sortDescending ? -1 : 1;
        return [...filteredValues].sort((a, b) => {
            const aValue = a?.[sortField];
            const bValue = b?.[sortField];
            if (typeof aValue === 'number' && typeof bValue === 'number') return (aValue - bValue) * direction;
            return String(aValue ?? '').localeCompare(String(bValue ?? ''), undefined, {numeric: true, sensitivity: 'base'}) * direction;
        });
    }, [filteredValues, sortField, sortDescending])

    const pageSize = pageSizeSetting === 'fit' ? fitPageSize : pageSizeSetting;
    const pages = Math.ceil(sortedValues.length / pageSize);
    // A shrinking fit size or filter can leave the current page past the end; clamp instead of resetting.
    const currentPage = Math.min(page, Math.max(pages, 1));

    const pageItems = useMemo(
        () => sortedValues.slice((currentPage - 1) * pageSize, currentPage * pageSize),
        [sortedValues, currentPage, pageSize])

    // Re-mark the chosen row when paging/sorting/filtering swaps the visible items out from under the Selection.
    useEffect(() => {
        if (!selectedIdRef.current) return;
        const index = pageItems.findIndex((i) => i["Id"]?.toString() === selectedIdRef.current);
        if (index >= 0 && !selection.isIndexSelected(index)) {
            selection.setIndexSelected(index, true, false);
        }
    }, [pageItems, selection])

    const columns: IColumn[] = useMemo(() => {
        const keys = new Set<string>();
        optionValues.forEach((o) => Object.keys(o).forEach((k) => keys.add(k)));
        const openColumn: IColumn[] = crmRecordType ? [{
            key: '__openExternal',
            name: '',
            minWidth: 32,
            maxWidth: 32,
            onRender: (item: IDictionary<string | number | boolean>) => (
                <IconButton
                    iconProps={{iconName: 'OpenInNewWindow'}}
                    title={`Open ${humanizeRecordType(crmRecordType)} in CRM`}
                    ariaLabel={`Open ${humanizeRecordType(crmRecordType)} in CRM`}
                    styles={{root: {height: 'auto'}}}
                    onClick={(e) => {
                        e.stopPropagation();
                        void openInCrm(item);
                    }}
                />
            )
        }] : [];
        const workspaceColumn: IColumn[] = workspaceLinksByRecord && Object.keys(workspaceLinksByRecord).length > 0 ? [{
            key: '__linkedWorkspace',
            name: 'Workspace',
            minWidth: 120,
            maxWidth: 180,
            isResizable: true,
            onRender: (item: IDictionary<string | number | boolean>) => {
                const link = workspaceLinksByRecord[item["Id"]?.toString() ?? ''];
                if (!link) return null;
                return (
                    <a
                        href={`/workspace/${link.workspaceId}`}
                        target='_blank'
                        rel='noreferrer'
                        title='This record was already imported — open its workspace'
                        onClick={(e) => e.stopPropagation()}
                    >
                        {link.workspaceName || 'Workspace'}
                    </a>
                );
            }
        }] : [];
        return [...openColumn, ...workspaceColumn, ...Array.from(keys).map((key) => ({
            key: key,
            name: key,
            fieldName: key,
            minWidth: 100,
            maxWidth: 250,
            isResizable: true,
            isMultiline: true,
            onRender: (item: IDictionary<string | number | boolean>) => <>{formatCellValue(key, item[key])}</>,
            onRenderHeader: () => (
                <FilterableSortableColumnHeader
                    label={key}
                    field={key}
                    sortable={true}
                    filterable={true}
                    sortedDescending={sortField === key ? sortDescending : undefined}
                    filterValue={filterField === key ? filterValue : ''}
                    onSort={() => sortColumn(key)}
                    onFilterApply={(filter) => applyFilter(key, filter)}
                />
            ),
        }))];
    }, [optionValues, sortField, sortDescending, filterField, filterValue, crmRecordType, workspaceLinksByRecord])

    const startCount = sortedValues.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endCount = Math.min(currentPage * pageSize, sortedValues.length);

    const changePageSize = (size: number | 'fit') => {
        setPageSizeSetting(size)
        setPage(1)
    }

    const pageCommands: ICommandBarItemProps[] = Array.from({ length: Math.min(pages, 10) }, (_, i) => ({
        key: `page-${i + 1}`,
        text: `Page ${i + 1}`,
        checked: currentPage === i + 1,
        onClick: () => setPage(i + 1),
        disabled: currentPage === i + 1
    }))

    const paginationCommands: ICommandBarItemProps[] = [
        {
            key: 'previous',
            text: 'Previous',
            iconOnly: true,
            iconProps: { iconName: 'ChevronLeft' },
            disabled: currentPage <= 1,
            onClick: () => setPage(currentPage - 1)
        },
        {
            key: 'page',
            text: sortedValues.length > 0
                ? (sortedValues.length === optionValues.length
                    ? `${startCount}-${endCount} of ${optionValues.length}`
                    : `${startCount}-${endCount} of ${sortedValues.length} (filtered from ${optionValues.length})`)
                : 'No Results',
            subMenuProps: {
                items: [
                    ...pageCommands,
                    ...(pageCommands.length > 0 ? [{
                        key: "divider",
                        itemType: ContextualMenuItemType.Divider
                    }] : []),
                    {
                        key: "sectionTitlePageSize",
                        text: "Select Page Size",
                        itemType: ContextualMenuItemType.Header
                    },
                    {
                        key: 'pageSizeFit',
                        text: 'Fit to Window',
                        onClick: () => changePageSize('fit'),
                        disabled: pageSizeSetting === 'fit'
                    },
                    ...[25, 50, 100, 250].map((size) => ({
                        key: `pageSize${size}`,
                        text: `${size}`,
                        onClick: () => changePageSize(size),
                        disabled: pageSizeSetting === size
                    }))
                ]
            }
        },
        {
            key: 'next',
            text: 'Next',
            iconOnly: true,
            iconProps: { iconName: 'ChevronRight' },
            disabled: currentPage >= pages,
            onClick: () => setPage(currentPage + 1)
        }
    ]

    if (isLoading && optionValues.length === 0) {
        return <Spinner
            size={SpinnerSize.large}
            label={`Loading ${props.field.description}...`}
            styles={{root: {margin: '3em 0'}}}
        />
    }

    if (loadError && optionValues.length === 0) {
        return <MessageBar messageBarType={MessageBarType.error}>{loadError}</MessageBar>
    }

    const paginationBar = <CommandBar items={[]} farItems={paginationCommands} styles={{root: {padding: 0}}} />;

    return (<>
        {loadError && <MessageBar messageBarType={MessageBarType.error}>{loadError}</MessageBar>}
        {props.paginatorHost
            ? ReactDOM.createPortal(paginationBar, props.paginatorHost)
            : paginationBar}
        <div ref={listStartRef}>
            {sortedValues.length === 0 && !isLoading
                ? <Text>No results match the current filter.</Text>
                : <ShimmeredDetailsList
                    items={pageItems}
                    columns={columns}
                    enableShimmer={isLoading}
                    selectionMode={SelectionMode.single}
                    selection={selection}
                    selectionPreservedOnEmptyClick={true}
                />}
        </div>
        </>
    );
}

export default ExtensionSearchResults;
